import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  createEmptyPortfolioInstructions,
  getPortfolioWithGuaranteedInstructions,
} from "@/composables/usePortfolioUtils";
import { createDefaultActionInstructions } from "@/dashboard/production_strategy/overview/PPA/ppaActionUtils";
import type { Company, Portfolio, PortfolioWithInstructions } from "@/types";
import { defineStore } from "pinia";
import { computed, readonly, ref } from "vue";
import { useI18n } from "vue-i18n";

type PortfolioType = "PPA" | "HOLD";

const REPEATED_ARRAY_PARAMS_SERIALIZER = { indexes: null };

export const useProductionStrategyStore = defineStore(
  "productionStrategy",
  () => {
    const { t } = useI18n();

    // State
    const isLoadingPortfolios = ref(false);

    // Portfolios
    const portfolios = ref<Portfolio[]>([]);

    // Computed properties
    const defaultHold = computed(() => {
      const existingHoldPortfolio = portfolios.value.find(
        (p) => p.portfolio_type === "HOLD" && p.is_default
      );

      if (existingHoldPortfolio) {
        return getPortfolioWithGuaranteedInstructions(existingHoldPortfolio);
      }

      return null;
    });

    /**
     * Derives duration in months from SPOT_MIN_PRICE portfolio duration_from_issuance.
     * Returns 6 as default if no portfolio exists or malformed duration.
     */
    const getDurationMonthsForCompany = (companyId: number): number => {
      const minPricePortfolio = portfolios.value.find(
        (p) =>
          p.company_id === companyId && p.portfolio_type === "SPOT_MIN_PRICE"
      );

      if (!minPricePortfolio?.portfolio_instructions?.duration_from_issuance) {
        return 6; // Default duration
      }

      const duration =
        minPricePortfolio.portfolio_instructions.duration_from_issuance;
      const match = duration.match(/^P(\d{1,2})M$/);

      if (match) {
        const months = parseInt(match[1], 10);
        return months >= 1 && months <= 12 ? months : 6;
      }

      return 6; // Fallback to default
    };

    // Helper functions
    async function withLoading<T>(
      loadingRef: typeof isLoadingPortfolios,
      operation: () => Promise<T>
    ): Promise<T> {
      try {
        loadingRef.value = true;
        return await operation();
      } finally {
        loadingRef.value = false;
      }
    }

    function updatePortfolioInArray(
      portfolios: Portfolio[],
      portfolio: PortfolioWithInstructions,
      portfolioType?: PortfolioType
    ): Portfolio[] {
      if (portfolio.id) {
        // Existing portfolio – update by ID
        const existingIndex = portfolios.findIndex(
          (p) => p.id === portfolio.id
        );
        if (existingIndex >= 0) {
          return [
            ...portfolios.slice(0, existingIndex),
            portfolio,
            ...portfolios.slice(existingIndex + 1),
          ];
        } else {
          return [...portfolios, portfolio];
        }
      } else if (portfolioType === "HOLD") {
        // Target the default HOLD – non-default HOLDs are passive (split/merge artifacts)
        const existingIndex = portfolios.findIndex(
          (p) => p.portfolio_type === "HOLD" && p.is_default
        );
        if (existingIndex >= 0) {
          return [
            ...portfolios.slice(0, existingIndex),
            portfolio,
            ...portfolios.slice(existingIndex + 1),
          ];
        } else {
          return [...portfolios, portfolio];
        }
      } else {
        // New PPA or other multi-instance portfolio – append as new
        return [...portfolios, portfolio];
      }
    }

    // Actions

    async function savePortfolios(
      companyId: number,
      updatedPortfolios: PortfolioWithInstructions[]
    ): Promise<boolean> {
      return withLoading(isLoadingPortfolios, async () => {
        const response = await goSolidApi.post(
          `/api/company/${companyId}/portfolios`,
          { portfolios: updatedPortfolios }
        );

        if (response.status === 200) {
          portfolios.value = response.data.portfolios;
          return true;
        }
        return false;
      });
    }

    function createPortfolio(
      companyId: number,
      type: PortfolioType,
      name?: string
    ): PortfolioWithInstructions {
      return {
        name:
          name || (type === "HOLD" ? t("hold_strategy.hold_portfolio") : ""),
        portfolio_type: type,
        is_default: false,
        company_id: companyId,
        portfolio_instructions: createEmptyPortfolioInstructions(
          type === "PPA" ? 100 : 0
        ),
        custom_user_upload_id: null,
        action_instructions:
          type === "PPA" ? createDefaultActionInstructions() : undefined,
      };
    }

    // Portfolio creation helpers
    const createNewPPAPortfolio = (companyId: number) =>
      createPortfolio(companyId, "PPA");
    const createHoldPortfolio = (companyId: number) =>
      createPortfolio(companyId, "HOLD");

    async function savePortfolio(
      companyId: number,
      portfolio: PortfolioWithInstructions
    ): Promise<boolean> {
      return withLoading(isLoadingPortfolios, async () => {
        const updatedPortfolios = updatePortfolioInArray(
          portfolios.value,
          portfolio,
          portfolio.portfolio_type as PortfolioType
        );

        return await savePortfolios(
          companyId,
          updatedPortfolios as PortfolioWithInstructions[]
        );
      });
    }

    // Portfolio saving alias
    const saveHoldPortfolio = savePortfolio;

    // Helper functions for saveSpotStrategy
    function ensureSpotMinPortfolio(
      portfolios: Portfolio[],
      companyId: number,
      price: number,
      durationMonths = 6
    ): void {
      let min: Portfolio | undefined = portfolios.find(
        (p) => p.portfolio_type === "SPOT_MIN_PRICE"
      );

      if (min === undefined) {
        min = {
          name: "Spot with Minimum Price",
          company_id: companyId,
          portfolio_type: "SPOT_MIN_PRICE",
          is_default: false,
          portfolio_instructions: {
            allocate_percentage: 0,
            limit_price: price.toFixed(3),
            duration_from_issuance: `P${durationMonths}M`,
            registry_device_ids: [],
            limits: [],
          },
          custom_user_upload_id: null,
        };
        portfolios.push(min);
      }
    }

    function applySpotAllocations(
      portfolios: Portfolio[],
      strategy: {
        type: "market" | "minimum";
        minimumPrice?: number;
        durationMonths?: number;
      }
    ): void {
      const durationMonths = strategy.durationMonths ?? 6;

      if (strategy.type === "minimum") {
        // Set allocations: MIN=100, MARKET=0
        portfolios.forEach((p) => {
          if (!p.portfolio_instructions) {
            p.portfolio_instructions = createEmptyPortfolioInstructions();
          }
          if (p.portfolio_type === "SPOT_MIN_PRICE") {
            p.portfolio_instructions.allocate_percentage = 100;
            p.portfolio_instructions.limit_price = (
              strategy.minimumPrice ?? 0
            ).toFixed(3);
            p.portfolio_instructions.duration_from_issuance = `P${durationMonths}M`;
          }
          if (p.portfolio_type === "SPOT_MARKET_PRICE") {
            p.portfolio_instructions.allocate_percentage = 0;
          }
        });
      } else {
        // Market strategy: MIN=0, MARKET=100 (keep MIN portfolio but set to 0)
        portfolios.forEach((p) => {
          if (!p.portfolio_instructions) {
            p.portfolio_instructions = createEmptyPortfolioInstructions();
          }
          if (p.portfolio_type === "SPOT_MIN_PRICE") {
            p.portfolio_instructions.allocate_percentage = 0;
          }
          if (p.portfolio_type === "SPOT_MARKET_PRICE") {
            p.portfolio_instructions.allocate_percentage = 100;
          }
        });
      }
    }

    async function saveSpotStrategy(
      companyId: number,
      strategy: {
        type: "market" | "minimum";
        minimumPrice?: number;
        durationMonths?: number;
      }
    ): Promise<boolean> {
      return withLoading(isLoadingPortfolios, async () => {
        // Load current portfolios if not loaded
        if (portfolios.value.length === 0) {
          await _fetchAggregatedPortfolios([companyId]);
        }

        // Filter portfolios to only include those belonging to the target company
        const current = portfolios.value.filter(
          (portfolio) => portfolio.company_id === companyId
        );

        if (strategy.type === "minimum") {
          ensureSpotMinPortfolio(
            current,
            companyId,
            strategy.minimumPrice ?? 0,
            strategy.durationMonths ?? 6
          );
        }

        // Apply strategy allocations
        applySpotAllocations(current, strategy);

        return await savePortfolios(
          companyId,
          current as unknown as PortfolioWithInstructions[]
        );
      });
    }

    async function refetchPortfolios(companyIds: number[]) {
      if (companyIds.length === 0) return;
      await _fetchAggregatedPortfolios(companyIds);
    }

    async function refreshMultiCompanyData(companies: Company[]) {
      if (companies.length === 0) {
        portfolios.value = [];
        return;
      }

      const companyIds = companies
        .map((company) => company.id)
        .filter((id): id is number => id !== undefined);

      await _fetchAggregatedPortfolios(companyIds);
    }

    async function _fetchAggregatedPortfolios(companyIds: number[]) {
      return withLoading(isLoadingPortfolios, async () => {
        if (companyIds.length === 1) {
          const response = await goSolidApi.get(
            `/api/company/${companyIds[0]}/portfolios`
          );
          portfolios.value = response.data.portfolios ?? [];
          return;
        }

        const response = await goSolidApi.get("/api/companies/portfolios", {
          params: { company_ids: companyIds },
          paramsSerializer: REPEATED_ARRAY_PARAMS_SERIALIZER,
        });
        const groupedPortfolios = response.data.portfolios ?? {};
        portfolios.value = Object.values(
          groupedPortfolios
        ).flat() as Portfolio[];
      });
    }

    return {
      // State
      isLoadingPortfolios: readonly(isLoadingPortfolios),
      portfolios: readonly(portfolios),

      // Computed
      defaultHold,

      // Helper functions
      getDurationMonthsForCompany,

      // Actions
      savePortfolios,
      savePortfolio,
      saveSpotStrategy,
      saveHoldPortfolio,
      createPortfolio,
      createNewPPAPortfolio,
      createHoldPortfolio,
      refetchPortfolios,
      refreshMultiCompanyData,
    };
  }
);
